import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../config/api';
import { writeLanguageChoice } from '../i18n/detectLang';
import { SUPPORTED_LANGS } from '../i18n/config';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';

// =============================================================================
// Funnel V3 — 3 pages utiles + 1 page de remerciement
// -----------------------------------------------------------------------------
// Étapes du funnel V3 (cf. brief Funnel V3 — 22 mai 2026) :
//
//   PAGE 1 — SATISFACTION
//     « Comment s'est passée votre expérience ? »
//     3 boutons : 😊 Très satisfait · 😐 Correct · 🙁 Déçu
//     Valeurs DB inchangées : excellent / bien / moyen (préservation analytics).
//     Aucun POST. Clic → page 2.
//
//   PAGE 2 — GOOGLE + FEEDBACK PRIVÉ
//     [⭐ Laisser un avis Google]       ← visible pour 100% des clients (CGU)
//     ── ou ──
//     Bulle texte (placeholder dynamique selon satisfaction)
//     [Envoyer mon retour au restaurateur →]
//
//     Clic Google      → POST {parcours:'google', satisfaction} → ouvre Google → CONFIRMATION
//     Clic "Envoyer"   → POST {parcours:'feedback', satisfaction, feedback_prive} → COORDONNEES
//                        (récup client_qr_id pour le PATCH suivant)
//
//   PAGE 3 — COORDONNÉES + RPS (parcours feedback uniquement)
//     Slider RPS 0-100 + prénom + tabs SMS/email + Valider
//     Clic Valider     → PATCH /contact/:client_qr_id avec rps_score → CONFIRMATION
//     C'est ICI que le back envoie le SMS/mail immédiat dont le contenu dépend
//     du RPS : ≥70 → remerciement + lien Google · <70 → suivi privé sans lien.
//
//   PAGE 4 — CONFIRMATION
//     Message personnalisé selon parcours / satisfaction.
//
// Conformité CGU Google : bouton Google strictement identique pour tous les
// clients en page 2 — aucune dépendance à la satisfaction (taille, couleur, position).
//
// Progress dots : 3 points (page 1 / page 2 / page 3). La confirmation n'est pas un point.
//
// i18n Phase 2 — toutes les strings visibles passent par le namespace 'funnel'
// (frontend/src/i18n/locales/<lang>/funnel.json). La langue est :
//   1. forcée par ?lang=xx dans l'URL si présent (écrasement cascade i18n)
//   2. sinon issue de la détection initiale (geoip / navigator / fallback FR)
// Cette langue est propagée au backend via le body { lang } sur POST /submit
// et PATCH /contact pour que l'email/SMS Brevo soit envoyé dans la bonne langue.
// =============================================================================

const STEPS = {
  SATISFACTION: 'step-satisfaction',
  GOOGLE_FEEDBACK: 'step-google-feedback',
  COORDONNEES: 'step-coordonnees',
  CONFIRMATION: 'step-confirmation'
};

// Génère un UUID v4. Utilise crypto.randomUUID() si dispo (Chrome 92+,
// Safari 15.4+, Firefox 95+), sinon fallback Math.random conforme RFC4122
// (suffisant pour idempotency, pas pour des secrets cryptographiques).
function generateIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// URL d'avis Google. Priorité : formulaire writereview direct (via
// google_place_id) → fallback fiche Google Maps → null (pas de lien).
// IDENTIQUE pour tous les clients quel que soit la satisfaction / le RPS
// (conformité CGU Google — aucune URL conditionnelle). Sortie en helper
// module-scope pour être réutilisée à la fois par selectSat (raccourci
// "Très satisfait") et par le rendu de la page 2.
function buildReviewUrl(restaurant) {
  if (!restaurant) return null;
  return restaurant.google_place_id
    ? `https://search.google.com/local/writereview?placeid=${restaurant.google_place_id}`
    : restaurant.google_maps_url || null;
}

export default function QRPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Namespace 'funnel' couvre toutes les strings visibles (page 1→4 + erreurs).
  const { t, i18n } = useTranslation('funnel');
  // Pré-remplissage du prénom depuis l'URL (Session 8 : SMS de collecte envoie
  // ?prenom=Marie&from=collect). Lu une seule fois à l'init du state pour ne pas
  // écraser la saisie de l'utilisateur si un re-render a lieu.
  const initialPrenom = (searchParams.get('prenom') || '').slice(0, 50).trim();
  const fromCollect = searchParams.get('from') === 'collect';

  const [restaurant, setRestaurant] = useState(null);
  const [step, setStep] = useState(STEPS.SATISFACTION);
  const [satisfaction, setSatisfaction] = useState('');
  // Slider RPS — défaut 70 (= seuil "satisfait", évite biais à la baisse)
  const [rpsScore, setRpsScore] = useState(70);
  const [feedback, setFeedback] = useState('');
  const [prenom, setPrenom] = useState(initialPrenom);
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [contactType, setContactType] = useState('sms');
  const [loading, setLoading] = useState(false);
  // formErrors : feedback inline NON BLOQUANT (le bouton reste actif même si erreur)
  const [formErrors, setFormErrors] = useState({});
  // client_qr_id récupéré au POST /submit (parcours feedback) — sert au PATCH ensuite.
  const [clientQrId, setClientQrId] = useState(null);
  // Anti double-clic sur les boutons d'action page 2 (Google / Feedback).
  const submittedRef = useRef(false);

  // Clé idempotency persistante pour cette session de scan. 1 par scan =
  // suffit pour dédupliquer un double-clic. Régénérée si l'utilisateur
  // recharge la page (cas légitime, nouvelle intention).
  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  // === i18n P2 — override URL ?lang=xx ===
  // Si le restaurateur imprime un QR avec ?lang=en (menu anglais dédié), on
  // force la langue cible et on la persiste. SUPPORTED_LANGS = whitelist
  // côté config.js — toute autre valeur est ignorée silencieusement.
  useEffect(() => {
    const fromUrl = (searchParams.get('lang') || '').toLowerCase();
    if (fromUrl && SUPPORTED_LANGS.includes(fromUrl) && fromUrl !== i18n.language) {
      writeLanguageChoice(fromUrl);
      i18n.changeLanguage(fromUrl);
    }
  }, [searchParams, i18n]);

  useEffect(() => {
    fetch(`${API_BASE}/api/r/${slug}`)
      .then(r => r.json())
      .then(d => setRestaurant(d))
      .catch(e => console.error('Erreur chargement restaurant:', e));
  }, [slug]);

  useEffect(() => {
    if (fromCollect) {
      console.log('[QRPage V3] Visite via SMS de collecte (prenom=' + initialPrenom + ')');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validations (utilisées en NON BLOQUANT — tolérance brief V3)
  const validerTelephone = (tel) => /^0[1-9][0-9]{8}$/.test(tel.replace(/[\s.\-]/g, ''));
  const validerEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em.trim());

  // Step SATISFACTION : clic emoji.
  //   • "excellent" (Très satisfait) → RACCOURCI : POST métrique (parcours google,
  //     keepalive) PUIS redirection DIRECTE vers le formulaire d'avis Google.
  //     On saute la page 2 — le client satisfait va laisser son avis sans friction.
  //   • "bien" (Correct) / "moyen" (Déçu) → parcours classique vers la page 2
  //     (avis Google OU feedback privé), où le bouton Google reste visible pour
  //     100% des clients (conformité CGU Google — pas de gating dur).
  // IMPORTANT : les VALEURS DB ('excellent','bien','moyen') sont préservées pour ne
  // pas casser les SELECT existants (dashboard.js, feedbacks_negatifs JOIN, etc.).
  // Seuls les LABELS UI changent (Très satisfait / Correct / Déçu).
  const selectSat = (level) => {
    setSatisfaction(level);

    // --- Raccourci satisfait : redirection directe Google ---
    if (level === 'excellent') {
      const url = buildReviewUrl(restaurant);
      if (url) {
        // Garde anti double-clic (réutilise le ref partagé avec la page 2).
        if (submittedRef.current) return;
        submittedRef.current = true;
        // POST en keepalive (survit à la redirection) puis navigation même onglet :
        // le plus robuste en WebView in-app où window.open est souvent bloqué.
        postSubmit('google', { satisfaction: level, keepalive: true }).catch(() => {});
        window.location.href = url;
        return;
      }
      // Pas d'URL Google configurée pour ce resto → on retombe proprement sur le
      // parcours page 2 classique (le bouton "Continuer" y est affiché).
    }

    // setTimeout 200ms conservé pour l'effet visuel "sélectionné" avant transition.
    setTimeout(() => setStep(STEPS.GOOGLE_FEEDBACK), 200);
  };

  // === Page 2 — Helper POST submit ===
  // Centralise l'appel POST /:slug/submit avec idempotency. Renvoie le client_qr_id
  // ou null en cas d'erreur (le funnel continue même si l'API échoue — tolérance UX).
  // i18n P2 — `lang` ajouté au body pour persistance en DB (clients_qr.lang).
  const postSubmit = async (parcours, opts = {}) => {
    const body = {
      // opts.satisfaction : permet de passer la valeur AVANT que le state React
      // ne soit committé. Cas raccourci "Très satisfait" : le POST est déclenché
      // dans selectSat au même tick que setSatisfaction → `satisfaction` (state)
      // n'est pas encore à jour, on s'appuie donc sur l'override.
      satisfaction: opts.satisfaction || satisfaction,
      parcours,
      feedback_prive: opts.feedback || null,
      prenom: prenom || null,
      // En V3, le POST ne porte ni contact ni RPS — ces données arrivent au PATCH.
      // On envoie quand même `contact_type` pour rester cohérent côté DB (default 'sms').
      contact_type: 'sms',
      lang: i18n.language || 'fr'
    };
    try {
      const res = await fetch(`${API_BASE}/api/r/${slug}/submit`, {
        method: 'POST',
        // keepalive : laisse le POST se terminer même si la page est quittée juste
        // après (redirection window.location vers Google dans le raccourci satisfait).
        // Sans ça le navigateur annulerait la requête → métrique "google" perdue.
        keepalive: opts.keepalive || false,
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKeyRef.current
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        console.warn('[QRPage V3] submit HTTP ' + res.status + ' — on continue le funnel');
        return null;
      }
      const data = await res.json();
      return data.client_qr_id || null;
    } catch (err) {
      console.error('[QRPage V3] postSubmit error:', err);
      return null;
    }
  };

  // === Page 2 — Clic "Laisser un avis Google" ===
  // POST {parcours:'google'} (pour métriques back) PUIS ouverture du lien Google.
  // L'ouverture est gérée par le href de l'ancre (plus robuste en WebView).
  // On passe à CONFIRMATION en parallèle pour ne pas perdre le client si Google met
  // du temps à charger.
  const handleGoogleClick = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    // Fire-and-forget : on ne bloque pas la transition vers Google si le POST est lent.
    postSubmit('google').catch(() => {});
    setStep(STEPS.CONFIRMATION);
  };

  // === Page 2 — Clic "Envoyer mon retour au restaurateur" ===
  // POST {parcours:'feedback', feedback_prive} → récup client_qr_id → page 3.
  // Le feedback est ainsi SAUVÉ même si le client abandonne la page 3 (brief §1 critère 4).
  const handleEnvoyerRetour = async () => {
    if (submittedRef.current) return;
    // Garde anti-envoi à vide : le parcours feedback n'a de sens qu'avec un message.
    // Le bouton est aussi désactivé tant que la bulle est vide (cf. JSX) — ceci est le filet.
    if (!feedback.trim()) return;
    submittedRef.current = true;
    setLoading(true);
    try {
      const newClientQrId = await postSubmit('feedback', { feedback });
      setClientQrId(newClientQrId);
      setStep(STEPS.COORDONNEES);
      // Reset du flag : la page 3 a son propre garde anti double-PATCH.
      submittedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // === Page 3 — Clic "Valider" ===
  // PATCH /:slug/contact/:client_qr_id avec rps_score + coordonnées.
  // Le back décide du contenu du message (lien Google ou pas) selon RPS≥70.
  // i18n P2 — `lang` propagé pour que l'email/SMS Brevo soit dans la bonne langue.
  const handleValiderCoordonnees = async () => {
    if (submittedRef.current) return;

    // Validation NON BLOQUANTE — informe l'utilisateur mais ne bloque pas le flow
    // si le champ est vide (cas tolérance brief).
    const errs = {};
    if (contactType === 'sms' && telephone && !validerTelephone(telephone)) {
      errs.telephone = t('page3.error_telephone');
    }
    if (contactType === 'email' && email && !validerEmail(email)) {
      errs.email = t('page3.error_email');
    }
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      return; // Reste sur la page, le bouton reste actif pour réessai
    }

    // Champ contact vide → on laisse le client passer au remerciement sans PATCH
    // (pas de coordonnées = pas d'envoi de message, mais le feedback est déjà capturé).
    const hasContact = (contactType === 'sms' && telephone) || (contactType === 'email' && email);
    if (!hasContact || !clientQrId) {
      submittedRef.current = true;
      setStep(STEPS.CONFIRMATION);
      return;
    }

    submittedRef.current = true;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/r/${slug}/contact/${clientQrId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prenom: prenom || null,
          telephone: contactType === 'sms' ? (telephone || null) : null,
          email: contactType === 'email' ? (email || null) : null,
          contact_type: contactType,
          rps_score: rpsScore,
          lang: i18n.language || 'fr'
        })
      });
      if (!res.ok) {
        console.warn('[QRPage V3] contact PATCH HTTP ' + res.status);
      }
    } catch (err) {
      console.error('[QRPage V3] handleValiderCoordonnees error:', err);
    } finally {
      setLoading(false);
      setStep(STEPS.CONFIRMATION);
    }
  };

  // Step GOOGLE_FEEDBACK — URL d'avis Google (rendue en ancre <a>, robuste en
  // WebView in-app). Même helper que le raccourci satisfait → URL strictement
  // identique pour tous (conformité CGU : aucune redirection conditionnelle).
  const reviewUrl = buildReviewUrl(restaurant);

  // Helpers UI dérivés
  const estSatisfait = (
    satisfaction === 'excellent' ||
    satisfaction === 'bien'
  );

  // Label dynamique sous le slider RPS — purement informatif, n'affecte rien backend
  const rpsLabel = rpsScore >= 85
    ? t('page3.rps_label_excellent')
    : rpsScore >= 70
      ? t('page3.rps_label_tres_bien')
      : rpsScore >= 50
        ? t('page3.rps_label_correct')
        : rpsScore >= 30
          ? t('page3.rps_label_decevant')
          : t('page3.rps_label_tres_decevant');

  // Placeholder dynamique de la bulle feedback page 2 — brief §4 mot pour mot
  const feedbackPlaceholder = satisfaction === 'moyen'
    ? t('page2.feedback_placeholder_moyen')
    : satisfaction === 'excellent'
      ? t('page2.feedback_placeholder_excellent')
      : t('page2.feedback_placeholder_default');

  if (!restaurant) return <div style={{textAlign:'center',padding:'40px',fontFamily:'Sora,sans-serif'}}>{t('loading')}</div>;

  // === Personnalisation Page client (DB-backed avec fallback Revyo) ===
  // Cf. Sprint 1 P0 — couleurs/logo/messages stockés en DB par le restaurateur.
  // Champs validés côté backend (regex hex pour couleurs, https pour logo).
  const accentColor = restaurant.couleur_accent || '#E8502A';
  const bgColor = restaurant.couleur_fond || '#F6F4EF';
  const logoUrl = restaurant.logo_url || null;
  const msgAccueil = restaurant.message_accueil || '';

  const themeStyle = {
    '--accent': accentColor,
    '--bg': bgColor,
  };

  return (
    <>
      <style>{css}</style>
      <div className="phone-wrap" style={themeStyle}>
        <div className="screen">

          {/* ================================================================== */}
          {/* PAGE 1 SATISFACTION — wording brief V3, valeurs DB préservées      */}
          {/* ================================================================== */}
          {step === STEPS.SATISFACTION && (
            <div className="step active">
              <div className="resto-header">
                {/* i18n P2 — LanguageSwitcher compact positionné absolute en top-right
                    du header foncé. Visible dès la page 1 (= 1er paint du funnel)
                    pour qu'un touriste puisse changer de langue avant tout clic. */}
                <div className="lang-switcher-slot">
                  <LanguageSwitcher compact />
                </div>
                <div className="resto-logo">
                  {logoUrl
                    ? <img src={logoUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'14px'}} />
                    : '🍽'}
                </div>
                <div className="resto-name">{restaurant.nom}</div>
                <div className="resto-sub">{restaurant.ville || ''}</div>
              </div>
              <div className="step-body">
                <div className="step-title">
                  {prenom ? t('satisfaction.greeting_named', { prenom }) : t('satisfaction.greeting_anonymous')}
                </div>
                <div className="step-sub">
                  {msgAccueil || (prenom ? t('satisfaction.subtitle_named') : t('satisfaction.subtitle_default'))}
                </div>
                <div className="sat-options">
                  <button className={`sat-btn${satisfaction==='excellent'?' sel':''}`} onClick={() => selectSat('excellent')}>
                    <div className="sat-icon">😊</div>
                    <div className="sat-label"><strong>{t('satisfaction.excellent_title')}</strong><span>{t('satisfaction.excellent_subtitle')}</span></div>
                  </button>
                  <button className={`sat-btn${satisfaction==='bien'?' sel':''}`} onClick={() => selectSat('bien')}>
                    <div className="sat-icon">😐</div>
                    <div className="sat-label"><strong>{t('satisfaction.bien_title')}</strong><span>{t('satisfaction.bien_subtitle')}</span></div>
                  </button>
                  <button className={`sat-btn${satisfaction==='moyen'?' sel':''}`} onClick={() => selectSat('moyen')}>
                    <div className="sat-icon">🙁</div>
                    <div className="sat-label"><strong>{t('satisfaction.moyen_title')}</strong><span>{t('satisfaction.moyen_subtitle')}</span></div>
                  </button>
                </div>
                <div className="progress-dots">
                  <div className="dot active"></div><div className="dot"></div><div className="dot"></div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================== */}
          {/* PAGE 2 GOOGLE + FEEDBACK PRIVÉ                                     */}
          {/* Bouton Google universel (CGU) + bulle feedback → bouton secondaire */}
          {/* ================================================================== */}
          {step === STEPS.GOOGLE_FEEDBACK && (
            <div className="step active">
              <div className="step-body" style={{paddingTop:'28px'}}>
                {/* Switcher reste visible aussi sur la page 2 (top-right de l'écran clair). */}
                <div className="lang-switcher-slot lang-switcher-slot--inner">
                  <LanguageSwitcher compact />
                </div>
                <div style={{textAlign:'center',marginBottom:'22px'}}>
                  <div style={{fontSize:'40px',marginBottom:'8px'}}>
                    {satisfaction === 'excellent' ? '🌟' : satisfaction === 'moyen' ? '🙏' : '✨'}
                  </div>
                  <div className="step-title">
                    {prenom ? t('page2.title_thanks_named', { prenom }) : t('page2.title_thanks')}
                  </div>
                  <div className="step-sub">
                    {satisfaction === 'moyen'
                      ? t('page2.subtitle_moyen')
                      : (restaurant.nom
                          ? t('page2.subtitle_default', { nomResto: restaurant.nom })
                          : t('page2.subtitle_default_generic'))}
                  </div>
                </div>

                {/* --- Bloc Google (universel, CGU) --- */}
                <div className="google-block">
                  <div className="google-icon">G</div>
                  <div className="google-info">
                    <strong>{restaurant.nom}</strong>
                    <span>{(restaurant.ville || '') + ' · ' + t('page2.google_card_label')}</span>
                  </div>
                </div>

                {/* Bouton CTA Google strictement identique pour tous (CGU non négociable).
                    Rendu en <a> quand reviewUrl est dispo (robuste en WebView in-app).
                    Fallback bouton "Continuer" si pas de données Google → évite lien mort. */}
                {reviewUrl ? (
                  <a
                    className="cta-btn"
                    href={reviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleGoogleClick}
                    style={{display:'flex',textDecoration:'none'}}
                  >
                    {t('page2.google_button')}
                  </a>
                ) : (
                  <button className="cta-btn" onClick={handleGoogleClick}>
                    {t('page2.continue_button')}
                  </button>
                )}

                {/* --- Séparateur "ou" --- */}
                <div className="separator">
                  <span>{t('page2.separator')}</span>
                </div>

                {/* --- Bulle feedback (visible, vide, placeholder dynamique) --- */}
                <div className="phone-field" style={{marginBottom:'14px'}}>
                  <label>{t('page2.feedback_label')}</label>
                  <textarea
                    className="feedback-area"
                    placeholder={feedbackPlaceholder}
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    rows={3}
                  ></textarea>
                </div>

                {/* Bouton secondaire "Envoyer mon retour au restaurateur →" toujours visible (brief §2.1) */}
                <button
                  className="cta-btn cta-btn-secondary"
                  onClick={handleEnvoyerRetour}
                  disabled={loading || !feedback.trim()}
                >
                  {loading ? t('page2.submit_button_loading') : t('page2.submit_button')}
                </button>

                <div style={{textAlign:'center',fontSize:'11px',color:'var(--text3)',marginTop:'14px',lineHeight:'1.6'}}>
                  <a href="/politique-de-confidentialite" target="_blank" rel="noreferrer" style={{color:'var(--text3)',textDecoration:'underline'}}>{t('page2.privacy_link')}</a>
                </div>

                <div className="progress-dots" style={{paddingTop:'18px'}}>
                  <div className="dot done"></div><div className="dot active"></div><div className="dot"></div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================== */}
          {/* PAGE 3 COORDONNÉES + RPS (parcours feedback)                       */}
          {/* Slider + prénom + tabs SMS/email + Valider                         */}
          {/* ================================================================== */}
          {step === STEPS.COORDONNEES && (
            <div className="step active">
              <div className="step-body" style={{paddingTop:'28px'}}>
                <div className="lang-switcher-slot lang-switcher-slot--inner">
                  <LanguageSwitcher compact />
                </div>
                <div style={{textAlign:'center',marginBottom:'18px'}}>
                  <div style={{fontSize:'40px',marginBottom:'8px'}}>✍️</div>
                  <div className="step-title">{t('page3.title')}</div>
                  <div className="step-sub">
                    {t('page3.subtitle')}
                  </div>
                </div>

                {/* --- Slider RPS 0-100 --- */}
                <div className="rps-block">
                  <div className="rps-header">
                    <span className="rps-question">{t('page3.rps_question')}</span>
                  </div>
                  <div className="rps-value-row">
                    <div className="rps-value">{rpsScore}<span>{t('page3.rps_unit')}</span></div>
                    <div className="rps-label-tag">{rpsLabel}</div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={rpsScore}
                    onChange={e => setRpsScore(parseInt(e.target.value, 10))}
                    className="rps-slider"
                    aria-label={t('page3.rps_aria')}
                  />
                  <div className="rps-scale">
                    <span>0</span><span>50</span><span>100</span>
                  </div>
                </div>

                {/* --- Prénom --- */}
                <div className="phone-field" style={{marginTop:'22px'}}>
                  <label>{t('page3.prenom_label')}</label>
                  <div className="phone-input-wrap">
                    <input
                      type="text"
                      placeholder={t('page3.prenom_placeholder')}
                      value={prenom}
                      onChange={e => setPrenom(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                      style={{paddingLeft:'16px'}}
                    />
                  </div>
                </div>

                {/* --- Tabs SMS / Email + champ contact --- */}
                <div className="tabs">
                  <button className={`tab${contactType === 'sms' ? ' tab-active' : ''}`} onClick={() => setContactType('sms')}>{t('page3.tab_sms')}</button>
                  <button className={`tab${contactType === 'email' ? ' tab-active' : ''}`} onClick={() => setContactType('email')}>{t('page3.tab_email')}</button>
                </div>

                {contactType === 'sms' ? (
                  <div className="phone-field">
                    <label>{t('page3.telephone_label')}</label>
                    <div className="phone-input-wrap">
                      <div className="phone-flag">🇫🇷</div>
                      <input
                        type="tel"
                        placeholder={t('page3.telephone_placeholder')}
                        value={telephone}
                        onChange={e => { setTelephone(e.target.value); setFormErrors(prev => ({...prev, telephone: undefined})); }}
                        onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                        inputMode="tel"
                      />
                    </div>
                    {formErrors.telephone && <p style={{color:'#E8502A',fontSize:'12px',marginTop:'4px',marginBottom:'0'}}>{formErrors.telephone}</p>}
                  </div>
                ) : (
                  <div className="phone-field">
                    <label>{t('page3.email_label')}</label>
                    <div className="phone-input-wrap">
                      <input
                        type="email"
                        placeholder={t('page3.email_placeholder')}
                        value={email}
                        onChange={e => { setEmail(e.target.value); setFormErrors(prev => ({...prev, email: undefined})); }}
                        onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                        inputMode="email"
                        style={{paddingLeft:'16px'}}
                      />
                    </div>
                    {formErrors.email && <p style={{color:'#E8502A',fontSize:'12px',marginTop:'4px',marginBottom:'0'}}>{formErrors.email}</p>}
                  </div>
                )}

                <button className="cta-btn" onClick={handleValiderCoordonnees} disabled={loading} style={{marginTop:'10px'}}>
                  {loading ? t('page3.submit_button_loading') : t('page3.submit_button')}
                </button>

                <div style={{textAlign:'center',fontSize:'11px',color:'var(--text3)',marginTop:'14px',lineHeight:'1.6'}}>
                  {t('page3.consent_text')}<br/>
                  <a href="/politique-de-confidentialite" target="_blank" rel="noreferrer" style={{color:'var(--text3)',textDecoration:'underline'}}>{t('page3.privacy_link')}</a>
                </div>

                <div className="progress-dots" style={{paddingTop:'18px'}}>
                  <div className="dot done"></div><div className="dot done"></div><div className="dot active"></div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================== */}
          {/* PAGE 4 CONFIRMATION — message final personnalisé                   */}
          {/* (pas de progress dot ici)                                          */}
          {/* ================================================================== */}
          {step === STEPS.CONFIRMATION && (
            <div className="step active">
              <div className="success-wrap">
                <div className="success-icon">{estSatisfait ? '⭐' : '🙏'}</div>
                <div className="success-title">{prenom ? t('confirmation.title_named', { prenom }) : t('confirmation.title_anonymous')}</div>
                <div className="success-sub">
                  {estSatisfait
                    ? (restaurant.nom
                        ? <>{t('confirmation.body_satisfait_named', { nomResto: restaurant.nom })}<br/>{t('confirmation.body_satisfait_see_you')}</>
                        : <>{t('confirmation.body_satisfait_generic')}<br/>{t('confirmation.body_satisfait_see_you')}</>)
                    : (restaurant.nom
                        ? <>{t('confirmation.body_negatif_intro')}<br/><br/>{t('confirmation.body_negatif_see_you_named', { nomResto: restaurant.nom })}</>
                        : <>{t('confirmation.body_negatif_intro')}<br/><br/>{t('confirmation.body_negatif_see_you_generic')}</>)}
                </div>
              </div>
            </div>
          )}

        </div>
        <div className="powered">{t('footer.powered_by')} <a href="/">Revyo</a> · {t('footer.tagline')}</div>
      </div>
    </>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{--bg:#F6F4EF;--surface:#FFFFFF;--border:#EAE7DE;--text:#1A1710;--text2:#6E6C62;--text3:#AEACA4;--accent:#E8502A;--accent-light:#FCEEE8;--accent-mid:#F0B49A;--green:#0A7A45;--green-light:#E4F5EC;--gold:#F59E0B;--radius:20px;--radius-sm:12px;--shadow:0 2px 16px rgba(0,0,0,0.08);--shadow-lg:0 8px 32px rgba(0,0,0,0.12);}
body{font-family:'Sora',sans-serif;background:var(--bg);color:var(--text);min-height:100vh;font-size:15px;line-height:1.5;display:flex;align-items:center;justify-content:center;padding:20px;}
.phone-wrap{width:100%;max-width:390px;margin:0 auto;}
.screen{background:var(--surface);border-radius:var(--radius);box-shadow:var(--shadow-lg);overflow:hidden;position:relative;}
.step{animation:fadeIn 0.3s ease;}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
.resto-header{background:var(--text);padding:28px 24px 22px;text-align:center;position:relative;}
.resto-header::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;background:var(--accent);}
.resto-logo{width:56px;height:56px;border-radius:14px;background:var(--accent);margin:0 auto 10px;display:flex;align-items:center;justify-content:center;font-size:24px;}
.resto-name{font-size:18px;font-weight:700;color:white;letter-spacing:-0.5px;}
.resto-sub{font-size:12px;color:rgba(255,255,255,0.45);margin-top:2px;}

/* === i18n P2 — LanguageSwitcher positionnement ===
   Slot top-right de chaque step. Sur le resto-header (fond foncé), on
   inverse les couleurs du switcher via override pour garder le contraste.
   Sur les autres pages (fond clair), pas d'override : le style Aurora par
   défaut suffit. */
.lang-switcher-slot{position:absolute;top:12px;right:12px;z-index:5;}
.lang-switcher-slot--inner{margin:-8px -8px 8px auto;position:relative;top:auto;right:auto;display:flex;justify-content:flex-end;}
.resto-header .lang-switcher-slot button{background:rgba(255,255,255,0.12)!important;border-color:rgba(255,255,255,0.25)!important;color:#fff!important;}
.resto-header .lang-switcher-slot button:hover{background:rgba(255,255,255,0.22)!important;border-color:rgba(255,255,255,0.4)!important;}

.step-body{padding:24px 22px;}
.step-title{font-size:20px;font-weight:700;letter-spacing:-0.5px;margin-bottom:6px;color:var(--text);}
.step-sub{font-size:14px;color:var(--text2);line-height:1.55;margin-bottom:22px;}
.sat-options{display:flex;flex-direction:column;gap:10px;margin-bottom:22px;}
.sat-btn{padding:14px 18px;border-radius:var(--radius-sm);border:1.5px solid var(--border);background:var(--surface);font-size:14px;font-weight:500;color:var(--text);cursor:pointer;display:flex;align-items:center;gap:12px;transition:all 0.15s;font-family:inherit;width:100%;text-align:left;}
.sat-btn .sat-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0;background:var(--bg);}
.sat-btn:hover,.sat-btn.sel{background:var(--accent-light);border-color:var(--accent-mid);}
.sat-label{flex:1;}
.sat-label strong{display:block;font-size:14px;}
.sat-label span{font-size:12px;color:var(--text3);font-weight:400;}

/* === RPS slider (Funnel V3 — page 3) === */
.rps-block{background:var(--bg);border-radius:var(--radius-sm);padding:18px 16px;border:1.5px solid var(--border);}
.rps-header{margin-bottom:10px;}
.rps-question{font-size:13px;font-weight:600;color:var(--text);}
.rps-value-row{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:8px;}
.rps-value{font-size:36px;font-weight:700;color:var(--accent);letter-spacing:-1px;line-height:1;}
.rps-value span{font-size:14px;color:var(--text3);font-weight:500;margin-left:2px;}
.rps-label-tag{font-size:13px;font-weight:600;color:var(--accent);background:var(--accent-light);padding:4px 10px;border-radius:999px;}
.rps-slider{-webkit-appearance:none;appearance:none;width:100%;height:8px;border-radius:4px;background:linear-gradient(90deg,#E8502A 0%,#F59E0B 50%,#0A7A45 100%);outline:none;margin:6px 0 8px;cursor:pointer;}
.rps-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:28px;height:28px;border-radius:50%;background:#FFFFFF;border:3px solid var(--accent);cursor:pointer;box-shadow:0 2px 8px rgba(232,80,42,0.35);transition:transform 0.1s;}
.rps-slider::-webkit-slider-thumb:active{transform:scale(1.15);}
.rps-slider::-moz-range-thumb{width:28px;height:28px;border-radius:50%;background:#FFFFFF;border:3px solid var(--accent);cursor:pointer;box-shadow:0 2px 8px rgba(232,80,42,0.35);}
.rps-scale{display:flex;justify-content:space-between;font-size:11px;color:var(--text3);font-weight:500;}

.tabs{display:flex;gap:8px;margin-bottom:14px;background:#F6F4EF;border-radius:12px;padding:4px;}
.tab{flex:1;padding:10px 14px;background:transparent;border:none;border-radius:9px;font-size:13px;font-weight:600;color:#6E6C62;cursor:pointer;font-family:inherit;transition:all 0.15s;}
.tab:hover{color:#18160F;}
.tab-active{background:#E8502A;color:white;}
.tab-active:hover{color:white;}
.phone-field{margin-bottom:14px;}
.phone-field label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:var(--text3);margin-bottom:8px;}
.phone-input-wrap{display:flex;align-items:center;border:1.5px solid var(--border);border-radius:var(--radius-sm);overflow:hidden;background:var(--surface);transition:border-color 0.15s;}
.phone-input-wrap:focus-within{border-color:var(--accent);}
.phone-flag{padding:0 14px;font-size:18px;color:var(--text2);border-right:1.5px solid var(--border);height:50px;display:flex;align-items:center;background:var(--bg);}
.phone-input-wrap input{flex:1;padding:14px 16px;border:none;font-size:16px;font-family:inherit;color:var(--text);outline:none;background:transparent;letter-spacing:0.3px;}
.feedback-area{width:100%;padding:14px 16px;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-size:14px;font-family:inherit;color:var(--text);resize:none;min-height:80px;outline:none;background:var(--surface);transition:border-color 0.15s;}
.feedback-area:focus{border-color:var(--accent);}
.cta-btn{width:100%;padding:16px;border-radius:var(--radius-sm);background:#E8502A;color:white;font-size:15px;font-weight:600;border:none;cursor:pointer;font-family:inherit;transition:all 0.15s;display:flex;align-items:center;justify-content:center;gap:8px;letter-spacing:-0.2px;text-align:center;}
.cta-btn:hover{background:#CC4220;transform:translateY(-1px);box-shadow:0 6px 20px rgba(232,83,58,0.3);}
.cta-btn:disabled{opacity:0.5;cursor:not-allowed;transform:none;}
/* Bouton secondaire (Page 2 → Envoyer retour) : variante visuelle distincte du Google.
   Le Google reste le CTA primaire orange (CGU). Le secondaire est neutre/outline. */
.cta-btn-secondary{background:var(--surface);color:var(--text);border:1.5px solid var(--border);margin-top:6px;}
.cta-btn-secondary:hover{background:var(--bg);color:var(--text);transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,0.06);}
.google-block{background:var(--bg);border-radius:var(--radius-sm);padding:16px;margin-bottom:14px;display:flex;align-items:center;gap:14px;border:1.5px solid var(--border);}
.google-icon{width:40px;height:40px;border-radius:10px;background:white;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;border:1px solid var(--border);font-weight:700;color:#4285F4;}
.google-info strong{display:block;font-size:14px;color:var(--text);}
.google-info span{font-size:12px;color:var(--text3);}
/* Séparateur "ou" entre bouton Google et bulle feedback (page 2) */
.separator{display:flex;align-items:center;gap:12px;margin:18px 0;color:var(--text3);font-size:12px;text-transform:uppercase;letter-spacing:0.08em;font-weight:600;}
.separator::before,.separator::after{content:'';flex:1;height:1px;background:var(--border);}
.success-wrap{padding:36px 22px 32px;text-align:center;}
.success-icon{width:72px;height:72px;border-radius:50%;background:var(--green-light);display:flex;align-items:center;justify-content:center;font-size:32px;margin:0 auto 18px;color:var(--green);font-weight:700;}
.success-title{font-size:22px;font-weight:700;letter-spacing:-0.5px;margin-bottom:10px;}
.success-sub{font-size:14px;color:var(--text2);line-height:1.6;}
.progress-dots{display:flex;justify-content:center;gap:6px;padding:14px 0 0;}
.dot{width:6px;height:6px;border-radius:50%;background:var(--border);transition:all 0.3s;}
.dot.active{background:var(--accent);width:18px;border-radius:3px;}
.dot.done{background:var(--accent-mid);}
.powered{text-align:center;padding:14px 0 4px;font-size:11px;color:var(--text3);}
.powered a{color:var(--accent);text-decoration:none;font-weight:500;}
`;
